import { useCachedData } from '../hooks/useCachedData';
import { endpoints } from '../api/config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useMemo, useState, useEffect } from 'react';
import { 
  CircularProgress, 
  IconButton, 
  Tooltip, 
  Snackbar, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

const InboundShipment = () => {
  const { data, isLoading, error, refetch } = useCachedData(endpoints.inboundShipments);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inbound-shipments-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });
  const [itemDataDialogOpen, setItemDataDialogOpen] = useState(false);
  const [selectedItemData, setSelectedItemData] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [itemDataColumnVisibility, setItemDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('inbound-shipments-itemdata-column-visibility');
    return saved ? JSON.parse(saved) : {};
  });

  // Save column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('inbound-shipments-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  // Save item data column visibility to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('inbound-shipments-itemdata-column-visibility', JSON.stringify(itemDataColumnVisibility));
  }, [itemDataColumnVisibility]);

  // Handle refresh click
  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  // Transform the data to handle different response formats
  const tableData = useMemo(() => {
    if (!data) return [];
    
    // If data is an array, return as is
    if (Array.isArray(data)) {
      return data;
    }
    
    // If data is an object with numeric keys, convert to array
    if (typeof data === 'object') {
      // Check if it has numeric keys
      if (Object.keys(data).every(key => !isNaN(key))) {
        return Object.entries(data).map(([key, value]) => ({
          id: key,
          ...value
        }));
      }
      
      // If it has a data property that's an object with numeric keys
      if (data.data && typeof data.data === 'object') {
        if (Object.keys(data.data).every(key => !isNaN(key))) {
          return Object.entries(data.data).map(([key, value]) => ({
            id: key,
            ...value
          }));
        }
        return data.data;
      }
      
      // If it's a single object, wrap in array
      return [data];
    }
    
    return [];
  }, [data]);

  // Handle row click to open item data dialog
  const handleRowClick = (row) => {
    if (!row) return;
    
    // Check for itemData field (case-insensitive)
    const itemDataKey = Object.keys(row).find(k => 
      k.toLowerCase() === 'itemdata' || k.toLowerCase() === 'item_data'
    );
    
    if (itemDataKey) {
      const itemData = row[itemDataKey];
      if (Array.isArray(itemData) && itemData.length > 0) {
        setSelectedItemData(itemData);
        setSelectedRowData(row);
        setItemDataDialogOpen(true);
      }
    }
  };

  // Define columns for the table - using dynamic columns for flexibility
  const columns = useMemo(() => {
    // If we have data, generate columns with custom handling for itemData
    if (tableData.length > 0) {
      const firstRow = tableData[0];
      if (!firstRow) return [];
      
      // Get all keys from the data
      const allKeys = new Set();
      tableData.forEach(row => {
        Object.keys(row || {}).forEach(key => allKeys.add(key));
      });
      
      // Generate columns with special handling for itemData
      return Array.from(allKeys).map(key => {
        const headerText = key.split(/(?=[A-Z])/).map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ').replace(/_/g, ' ');
        
        // Special handling for itemData column
        if (key.toLowerCase() === 'itemdata' || key.toLowerCase() === 'item_data') {
          return {
            accessorKey: key,
            header: 'Item Data',
            minSize: 120,
            size: 150,
            enableSorting: false,
            cell: info => {
              const value = info.getValue();
              if (Array.isArray(value)) {
                return `${value.length} item${value.length !== 1 ? 's' : ''}`;
              }
              if (value && typeof value === 'object') {
                return '1 item';
              }
              return '-';
            }
          };
        }
        
        // For other columns, return standard column definition
        return {
          accessorKey: key,
          header: headerText,
          minSize: 120,
          size: 150,
          enableSorting: true
        };
      });
    }
    
    // Fallback columns if no data
    return [
      { 
        accessorKey: 'id', 
        header: 'ID',
        minSize: 150,
        size: 180,
        enableSorting: true
      },
      { 
        accessorKey: 'shipment_number', 
        header: 'Shipment Number',
        minSize: 180,
        size: 200,
        enableSorting: true
      },
      { 
        accessorKey: 'supplier', 
        header: 'Supplier',
        minSize: 200,
        size: 220,
        enableSorting: true
      },
      { 
        accessorKey: 'status', 
        header: 'Status',
        minSize: 120,
        size: 140,
        enableSorting: true
      },
      { 
        accessorKey: 'expected_date', 
        header: 'Expected Date',
        minSize: 150,
        size: 180,
        enableSorting: true
      },
      { 
        accessorKey: 'received_date', 
        header: 'Received Date',
        minSize: 150,
        size: 180,
        enableSorting: true
      },
      { 
        accessorKey: 'total_items', 
        header: 'Total Items',
        minSize: 120,
        size: 140,
        enableSorting: true
      },
      { 
        accessorKey: 'created_at', 
        header: 'Created At',
        minSize: 180,
        size: 200,
        enableSorting: true
      },
      { 
        accessorKey: 'updated_at', 
        header: 'Updated At',
        minSize: 180,
        size: 200,
        enableSorting: true
      }
    ];
  }, [tableData]);

  // Define columns for item data table
  const itemDataColumns = useMemo(() => {
    if (!selectedItemData || selectedItemData.length === 0) return [];
    
    // Get all unique keys from item data
    const allKeys = new Set();
    selectedItemData.forEach(item => {
      Object.keys(item || {}).forEach(key => allKeys.add(key));
    });
    
    // Define column order and labels
    const columnOrder = [
      'itemId',
      'itemName',
      'quantityExpected',
      'quantityRemaining',
      'quantityReceived',
      'purchaseOrderNumber',
      'vendorName',
      'itemUPC',
      'poId',
      'unit'
    ];
    
    // Create columns in specified order, then add any remaining keys
    const orderedKeys = [
      ...columnOrder.filter(key => allKeys.has(key)),
      ...Array.from(allKeys).filter(key => !columnOrder.includes(key))
    ];
    
    return orderedKeys.map(key => ({
      accessorKey: key,
      header: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim(),
      minSize: 120,
      size: 150,
      enableSorting: true,
      cell: info => {
        const value = info.getValue();
        if (value === null || value === undefined || value === '') return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      }
    }));
  }, [selectedItemData]);

  const inboundShipmentMainGrid = useDataGridProTable({
    data: tableData,
    loading: isLoading,
    error: error ? (error.message || 'Failed to load inbound shipments') : null,
    title: 'Inbound Shipments',
    columns,
    onDataRefresh: refetch,
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: handleRowClick,
    initialSorting: [{ id: 'id', desc: false }],
    muiTableContainerProps: { sx: { height: '100%', minHeight: 0 } },
    actionButtons: [
      <Tooltip title="Refresh Data" key="refresh-tooltip">
        <IconButton
          color="primary"
          onClick={handleRefreshClick}
          disabled={isRefreshing}
          sx={{ ml: 1 }}
        >
          {isRefreshing ? (
            <CircularProgress size={20} />
          ) : (
            <RefreshIcon />
          )}
        </IconButton>
      </Tooltip>,
    ],
  });

  const inboundShipmentItemDataGrid = useDataGridProTable({
    data: selectedItemData,
    columns: itemDataColumns,
    loading: false,
    title: 'inbound-shipment-itemdata',
    initialColumnVisibility: itemDataColumnVisibility,
    onColumnVisibilityChange: setItemDataColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: true,
    error: null,
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface hook={inboundShipmentMainGrid} DataGridPro={DataGridPro} />
      </Box>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Item Data Dialog */}
      <Dialog
        open={itemDataDialogOpen}
        onClose={() => {
          setItemDataDialogOpen(false);
          setSelectedItemData([]);
          setSelectedRowData(null);
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            width: '96vw',
            maxWidth: '96vw',
            height: '92vh',
            maxHeight: '92vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" component="div">
              Item Data
              {selectedRowData && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedRowData.shipment_number ? `Shipment: ${selectedRowData.shipment_number}` : ''}
                  {selectedRowData.id ? ` (ID: ${selectedRowData.id})` : ''}
                </Typography>
              )}
            </Typography>
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => {
                setItemDataDialogOpen(false);
                setSelectedItemData([]);
                setSelectedRowData(null);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {selectedItemData && selectedItemData.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={inboundShipmentItemDataGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No item data available
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default InboundShipment; 